import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import AmpLoadingSpinner from "../../components/common/AmpLoadingSpinner/AmpLoadingSpinner";
import PierTutorial from "../../components/login/PierTutorial/PierTutorial";
import { EnotificationPayload, registerUser, RegistrationPayload } from "../../redux/actions/auth/reg";
import { ROLL_AUTH } from "../../redux/actionTypes/actionsTypes";
import { RegisterUserResponse } from "../../redux/reducers/getAuthReducer/registerUserReducer";
import { RootState } from "../../redux/reducers/root";
import ReactGA from "react-ga4";

import "./Registration.css";
import AddressConfirm from "../../components/Registration/AddressConfirm/AddressConfirm";
import TocReg from "../../components/Registration/Toc/Toc";
import EmailPwd from "../../components/Registration/EmailPwd/EmailPwd";
import Security from "../../components/Registration/Security/Security";
import Preferences, { EnotifSettings } from "../../components/Registration/Language/Preferences";
import AttestationEnotificationAccept from "../../components/Registration/AttestationEnotifcation/Accept";
import SuccessOverlay from "../../components/common/SuccessOverlay/SuccessOverlay";
import addSuperScript from "../../utils/superscript";
import { setSessionExpiryReg } from "../../utils/helperFunction";
export default function Registration(): JSX.Element {

    const { t } = useTranslation('login');

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { token } = useParams();

    const [activeTab, setActiveTab] = useState(0);
    const [cancelSession, triggerCancel] = useState(false);

    const [securityFormValid, setSecFormValid] = useState(false);
    const [emailPwdFormValid, setEmailPwdFormValid] = useState(false);
    const [languageValid, setLanguageValid] = useState(false);

    const [regestrationValid, setRegValid] = useState(false);

    const valid = emailPwdFormValid && securityFormValid && languageValid;

    const enotificationChangeAllowed = localStorage.getItem("enotifAllowed");
    const enotifDefaultValue = localStorage.getItem("enotifDefault");
    
    const [sessionExpired, setSessionExpired] = useState(false);

    useEffect(() => {
        const timeLeft = setSessionExpiryReg();
        if (timeLeft <= 0) {
            if (!sessionExpired) {
                setSessionExpired(true);
            }
            else {
                navigate("auth/home")
            }
        } else {
                if (sessionExpired) {
                    setSessionExpired(false);
                }
                setTimeout(() => {
                    setSessionExpired(true);
                }, timeLeft );
        }
    }, [sessionExpired, navigate])



    const [payload, setPayload] = useState<RegistrationPayload>({
        "username": "",
        "password": "",
        "security_question": "",
        "is_custom": false,
        "security_answer": "",
        "lang_pref": "",
        "enotif": false
    })

    // Register User
    const registrationResult = useSelector((state: RootState) => state.authReducer.registration as RegisterUserResponse);

    useEffect(() => {
        const isResult = Object.keys(registrationResult.response).length > 0;
        const registrationSuccess = isResult ? registrationResult.success === true : true;
        if (!registrationSuccess) {
            ReactGA.event({
                category: "Login/Registration",
                action: "Registration Error",
                label: `${payload.username} Registration Account`, // optional
            });
        }

        if (isResult && registrationSuccess && !regestrationValid) {
            setRegValid(true);
            setTimeout(() => {
                if ([4, 5, 6].indexOf(activeTab) > -1) {
                    dispatch({ type: ROLL_AUTH.RESET });
                    navigate(`/auth/verify/${registrationResult.response["user_profile_id"]}/${payload.username}`);
                }
            }, 1500);
        }
    }, [registrationResult, payload, activeTab, dispatch, navigate, regestrationValid, t])

    // Cancel Registration Session when flag set
    if (cancelSession) {
        dispatch({ type: ROLL_AUTH.RESET });
        return <Navigate to="/auth/home" replace={true} />
    }

    // Prepares and dispatchs user registration
    const registerUserEvent = () => {
        if (valid) {
            console.log(payload);
            ReactGA.event({
                category: "Login/Registration",
                action: "Registration Account Creation",
                label: `${payload.username} Registration Account`, // optional
            });
            dispatch(registerUser(payload, (token as string)))
        }
    }
    const registerUserEventWithEnotifPayload = (enotif: EnotificationPayload) => {
        if (valid) {
            const finalPayload = {
                ...payload,
                ...enotif,
            }
            ReactGA.event({
                category: "Login/Registration",
                action: "Registration Account Creation",
                label: `${payload.username} Registration Account`, // optional
            });
            dispatch(registerUser(finalPayload, (token as string)))
        }
    }

    //eslint-disable-next-line
    const registrationError = "There was an error when registering the user";
    const hideStepHeaders = ([0, 1, 5, 6].indexOf(activeTab) > -1);

    const enotifPayload: EnotifSettings = {
        defaultValue: enotifDefaultValue === "true",
        isAllowed: enotificationChangeAllowed === "true"
    }
    return (
        <>
            <div className="auth-home-top-content">
                <span><h1>{addSuperScript(t("welcome") as string)}</h1></span>
                <div className={`registration-nav ${hideStepHeaders && "hidden"}`}>
                    <div className={`reg-nav-box ${activeTab === 2 ? "active" : ""}`}>
                        <span>{t("step")} 1 </span>
                        <span>{t("2-email-pwd")}</span>
                    </div>
                    <div className={`reg-nav-box ${activeTab === 3 ? "active" : ""}`}>
                        <span>{t("step")} 2 </span>
                        <span>{t("3-sec")}</span>
                    </div>
                    <div className={`reg-nav-box ${activeTab === 4 ? "active" : ""}`}>
                        <span>{t("step")} 3 </span>
                        <span>{t("4-lang")}</span>
                    </div>
                </div>
            </div>
            <div className="login-container-row">
                <div className="login-container">
                    <SuccessOverlay success={regestrationValid}>
                    <AmpLoadingSpinner loading={registrationResult.loading}>
                        <div className={`step-wrapper ${activeTab === 0 ? "active" : ""}`}>
                            <AddressConfirm triggerCancel={triggerCancel} setActiveTab={setActiveTab} />
                        </div>
                        <div className={`step-wrapper ${activeTab === 1 ? "active" : ""}`}>
                            <TocReg triggerCancel={triggerCancel} setActiveTab={setActiveTab} />
                        </div>
                        <div className={`step-wrapper ${activeTab === 2 ? "active" : ""}`}>
                            <EmailPwd
                                payload={payload}
                                setPayload={setPayload}
                                setEmailPwdFormValid={setEmailPwdFormValid}
                                triggerCancel={triggerCancel}
                                setActiveTab={setActiveTab}
                            />
                        </div>
                        <div className={`security- -tab step-wrapper ${activeTab === 3 ? "active" : ""}`}>
                            <Security
                                payload={payload}
                                setPayload={setPayload}
                                triggerCancel={triggerCancel}
                                setActiveTab={setActiveTab}
                                setSecFormValid={setSecFormValid}
                            />
                        </div>
                        <div className={`step-wrapper ${activeTab === 4 ? "active" : ""}`}>
                            <Preferences
                                setLanguageValid={setLanguageValid}
                                registerUserEvent={registerUserEvent}
                                payload={payload}
                                setPayload={setPayload}
                                triggerCancel={triggerCancel}
                                setActiveTab={setActiveTab} enotifSettings={enotifPayload}
                            />
                        </div>
                        <div className={`step-wrapper ${activeTab === 5 ? "active" : ""}`}>
                            <AttestationEnotificationAccept
                                setPayload={setPayload}
                                triggerCancel={triggerCancel}
                                registerUserEvent={registerUserEventWithEnotifPayload}
                                payload={payload}
                                setActiveTab={setActiveTab}
                            />
                        </div>
                        {/* <div className={`step-wrapper ${activeTab === 6 ? "active" : ""}`}>
                            <AttestationEnotificationDeny
                                setPayload={setPayload}
                                triggerCancel={triggerCancel}
                                registerUserEvent={registerUserEvent}
                                payload={payload}
                                setActiveTab={setActiveTab}
                            />
                        </div> */}
                    </AmpLoadingSpinner>
                    </SuccessOverlay>
                </div>
                <PierTutorial />
            </div>
            <div className="light-blue-bar span-page-blue-bar"></div>
        </>
    )
}